import React, { useEffect, useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import { Flex, Text } from "@radix-ui/themes";
import { StickyNote } from "lucide-react";
import { Skeleton } from "@radix-ui/themes";
import * as Toast from "@radix-ui/react-toast";
import { useAuth0 } from "@auth0/auth0-react";

import {
  useCreateOpportunityGroupFaqFeedback,
  useGetOpportunityGroup,
  useUpdateOpportunityGroup,
} from "@/api/useOpportunityGroups";
import {
  DateRange,
  DateRangePicker,
} from "@/components/shared/date-range-picker/DateRangePicker";
import Select from "@/components/shared/select";
import DataAccordion from "@/components/shared/data-accordion";
import CopyButton from "@/components/shared/copyButton";
import { KnowledgeGapTicketsTrend } from "./knowledge-gaps-table.types";
import {
  OpportunityStatus,
  opportunityStatusesToLabels,
} from "./index.types";
import { radixColors } from "@/components/radixColors";
import { TicketsInfo } from "@/features/tickets-info";
import { Feedback } from "@/features/feedback";
import { FeedbackType } from "@/features/feedback/index.types";

import styles from "../knowledge-gap/styles.module.scss";
import { AgentActivity } from "../knowledge-gap/agent";
import {
  ChangeStatusOptions,
  OpportunitiesGroup,
  OpportunityGroupIgnoreReasons,
} from "../knowledge-gap/index.types";
import { KnowledgeEvents } from "@/assets/mixpanel/knowledge";
import { mixTrackEvent } from "@/assets/mixpanel";

interface KnowledgeGapDetailProps {
  gapId: string;
  onClose: () => void;
  dateRange?: DateRange;
  allTimeSpanTickets?: number;
}

export const KnowledgeGapDetail = ({ gapId, onClose, dateRange: initialDateRange, allTimeSpanTickets }: KnowledgeGapDetailProps) => {
  const { user } = useAuth0();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const timerRef = useRef(0);
  const [openTicketsInfo, setOpenTicketsInfo] = useState(false);

  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange || {
      dateFrom: dayjs().subtract(2, "weeks").format("YYYY-MM-DD"),
      dateTo: dayjs().format("YYYY-MM-DD"),
    }
  );

  useEffect(() => {
    if (initialDateRange) {
      setDateRange(initialDateRange);
    }
  }, [initialDateRange]);
  const [status, setStatus] = useState<
    OpportunityStatus | OpportunityGroupIgnoreReasons | null
  >(null);
  const [faqFeedbackMap, setFaqFeedbackMap] = useState<
    Record<string, { comment: string; feedback: string | null }>
  >({});

  const { data: opportunityGroupData, isLoading } = useGetOpportunityGroup({
    id: gapId,
    startDate: dateRange.dateFrom,
    endDate: dateRange.dateTo,
    enabled: !!gapId,
  });
  const opportunityGroup = opportunityGroupData as OpportunitiesGroup;
  const { mutateAsync: updateOpportunityGroup } = useUpdateOpportunityGroup({
    opportunityGroupId: gapId,
  });
  const { mutateAsync: createOpportunityGroupFaqFeedback } =
    useCreateOpportunityGroupFaqFeedback();

  useEffect(() => {
    const value = opportunityGroup?.status;
    const ignoreReason = opportunityGroup?.hiddenReason;

    if (value === "hidden" && ignoreReason) {
      setStatus(ignoreReason);
    } else {
      setStatus(value);
    }
  }, [opportunityGroup]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleStatusChange = async (
    value: OpportunityStatus | OpportunityGroupIgnoreReasons
  ) => {
    mixTrackEvent({
      event: KnowledgeEvents.GAP_STATUS_CHANGED,
      properties: {
        opportunityGroupId: gapId,
        status: value,
        previousStatus: status,
      },
    });

    try {
      await updateOpportunityGroup({
        status: value === "hidden" ? "hidden" : (value as OpportunityStatus),
        hiddenReason:
          value !== "hidden" && value !== "backlog"
            ? undefined
            : (value as OpportunityGroupIgnoreReasons),
      });
      setStatus(value);
      setToastMessage("Status updated successfully");
    } catch (err) {
      setToastMessage("Status update failed");
    }
    setToastOpen(false);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setToastOpen(true);
    }, 100);
  };

  const faqItems = useMemo(() => {
    return (
      opportunityGroup?.faqs?.map((faq) => ({
        title: faq.question,
        content: faq.answer,
        bulletIcon: <StickyNote className="h-4 w-4 text-gray-500" />,
        actions: {
          button: (
            <CopyButton
              textToCopy={`${faq.question}\n\n${faq.answer}`}
              onClick={(value) => {
                mixTrackEvent({
                  event: KnowledgeEvents.FAQ_COPIED,
                  properties: {
                    opportunityGroupId: gapId,
                    faqId: faq.id,
                    faq: value,
                  },
                });
              }}
            />
          ),
          feedback: (
            <Feedback
              value={{
                comment:
                  faqFeedbackMap[faq.id]?.comment ||
                  faq?.faqFeedback[0]?.comment ||
                  "",
                type:
                  (faqFeedbackMap[faq.id]?.feedback as FeedbackType) ||
                  (faq?.faqFeedback[0]?.feedback as FeedbackType) ||
                  null,
              }}
              onChange={async ({ comment, type }) => {
                try {
                  mixTrackEvent({
                    event: KnowledgeEvents.FAQ_FEEDBACK_SUBMITTED,
                    properties: {
                      opportunityGroupId: gapId,
                      faqId: faq.id,
                      question: faq.question,
                      answer: faq.answer,
                      feedback: type,
                      comment,
                    },
                  });
                  await createOpportunityGroupFaqFeedback({
                    opportunityGroupFaqId: faq.id,
                    comment,
                    feedback: type,
                    author: user?.email ?? "",
                  });
                  setFaqFeedbackMap((prev) => ({
                    ...prev,
                    [faq.id]: { comment, feedback: type },
                  }));
                  setToastMessage("Feedback submitted successfully");
                } catch (err) {
                  setToastMessage("Feedback submission failed");
                }
                setToastOpen(false);
                window.clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => {
                  setToastOpen(true);
                }, 100);
              }}
            />
          ),
        },
      })) || []
    );
  }, [
    opportunityGroup,
    faqFeedbackMap,
    createOpportunityGroupFaqFeedback,
    user?.email,
    gapId,
  ]);

  const allFaqs = useMemo(() => {
    return opportunityGroup?.faqs
      ?.map((faq) => `${faq.question}\n\n${faq.answer}`)
      .join("\n\n");
  }, [opportunityGroup]);

  if (opportunityGroupData?.type === "Error") {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        p="8"
        gap="2"
        height="100%"
      >
        <Text size="3" color="red" weight="medium">
          Oops! Something went wrong
        </Text>
        <Text size="2" color="gray">
          We're sorry, but we couldn't load the knowledge gap details right now. Please try again later.
        </Text>
      </Flex>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.content}>
          <Flex
            gap={"4"}
            justify={"between"}
            align={"center"}
            width="100%"
          >
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Flex gap={"4"} justify={"between"} align={"center"}>
              <Flex
                gap={"2"}
                justify={"between"}
                align={"center"}
                onClick={() => {
                  mixTrackEvent({
                    event: KnowledgeEvents.GAP_RELATED_TICKETS_VIEWED,
                    properties: {
                      opportunityGroupId: gapId,
                    },
                  });
                  setOpenTicketsInfo(true);
                }}
                className={styles.relatedTicketsContainer}
              >
                <Flex gap={"2"} align={"center"}>
                  <Text size={"2"} weight={"light"}>
                    Related Tickets
                  </Text>
                  <Skeleton loading={isLoading}>
                    <Text size={"2"} className={styles.relatedTicketsNumber}>
                      {allTimeSpanTickets ?? opportunityGroup?.allTimeSpanTickets ?? opportunityGroup?.currentTimeSpanTickets ?? 0}
                    </Text>
                  </Skeleton>
                </Flex>
                <Flex gap={"1"} align={"center"}>
                  <Text size={"2"} weight={"light"} color="gray">
                    Selected Period
                  </Text>
                  <Skeleton loading={isLoading}>
                    <Text size={"2"} color="gray">
                      {opportunityGroup?.currentTimeSpanTickets ?? 0}
                    </Text>
                  </Skeleton>
                </Flex>
              </Flex>
              <Select
                options={ChangeStatusOptions}
                onChange={(value) =>
                  handleStatusChange(
                    value as unknown as
                      | OpportunityStatus
                      | OpportunityGroupIgnoreReasons
                  )
                }
                placeholder="Select a status"
                value={
                  status as string
                }
              />
            </Flex>
          </Flex>
          <Flex gap={"1"} width={"100%"} direction={"column"}>
            <Skeleton loading={isLoading}>
              <Text size={"8"} weight={"bold"} style={{ fontWeight: 500 }}>
                {isLoading ? "Loading..." : opportunityGroup?.label}
              </Text>
            </Skeleton>
            <Flex gap={"3"} width={"100%"}>
              {isLoading ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <KnowledgeGapTicketsTrend
                  ticketTrend={
                    opportunityGroup?.currentTimeSpanTickets -
                    opportunityGroup?.compareTimeSpanTickets
                  }
                />
              )}
            </Flex>
          </Flex>
          <Flex gap={"3"} width={"100%"} direction={"column"}>
            <Flex width={"100%"} justify={"between"}>
              <Text size={"3"} color="gray">
                Missing Content
              </Text>
              <CopyButton
                textToCopy={allFaqs}
                label="Copy All"
                onClick={(value) => {
                  mixTrackEvent({
                    event: KnowledgeEvents.ALL_FAQ_COPIED,
                    properties: {
                      opportunityGroupId: gapId,
                      allFaqs: value,
                    },
                  });
                }}
              />
            </Flex>
            <DataAccordion items={faqItems} loading={isLoading} />
          </Flex>
          {opportunityGroup?.topCollaborators?.length > 0 && (
            <Flex gap={"3"} width={"100%"} direction={"column"}>
              <Text size={"3"} color="gray">
                Agent Activity
              </Text>
              <Flex gap={"3"} width={"100%"}>
                {opportunityGroup?.topCollaborators.map((agent) => (
                  <AgentActivity key={agent.id} agent={agent} color={radixColors[0]} />
                ))}
              </Flex>
            </Flex>
          )}
        </div>
      </div>
      <TicketsInfo
        tickets={opportunityGroup?.relatedTickets}
        open={openTicketsInfo}
        setOpen={setOpenTicketsInfo}
      />
      <Toast.Provider swipeDirection="right">
        <Toast.Root
          className={styles.toastRoot}
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={2000}
        >
          <Toast.Title className={styles.toastTitle}>
            {toastMessage.includes("Status")
              ? "Status Update"
              : "Feedback Submitted"}
          </Toast.Title>
          <Toast.Description className={styles.toastDescription}>
            {toastMessage}
          </Toast.Description>
        </Toast.Root>
        <Toast.Viewport className={styles.toastViewport} />
      </Toast.Provider>
    </div>
  );
};

